import { Component, Input } from '@angular/core';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-user-limits',
  standalone: false,
  templateUrl: './user-limits.component.html',
  styleUrl: './user-limits.component.css',
})
export class UserLimitsComponent {
  @Input() uuid!: string;
  limits: any[] = [];
  submitted = false;
  isComplianceAdmin = false;

  showApprovalModal = false;
  selectedLimit: any = null;
  approvalAmount: number | null = null;
  isApproving = false;

  showCreateModal = false;
  createLimitAmount: number | null = null;
  isCreating = false;
  isRejecting = false;

  showMonthlyLimitModal = false;
  monthlyLimitAmount: number | null = null;
  isCreatingMonthlyLimit = false;
  isDeleting = false;

  constructor(
    private userService: UserService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.isComplianceAdmin = this.authService.isComplianceAdmin();
    this.loadLimits();
  }

  loadLimits() {
    this.submitted = true;

    this.userService.getTransactionLimits(this.uuid).subscribe({
      next: (response: any) => {
        this.limits = response;
        this.submitted = false;
      },
      error: () => {
        this.submitted = false;
      },
    });
  }

  canCreateOutboundLimit(): boolean {
    return !this.limits.some((limit) => limit.limit === 'SINGLE_OUTBOUND');
  }

  canApprove(limit: any): boolean {
    return (
      limit.limit === 'MONTHLY_INBOUND_CUMUL' &&
      limit.review_status === 'PENDING'
    );
  }

  openApprovalModal(limit: any) {
    this.selectedLimit = limit;
    this.approvalAmount = limit.limit_amount;
    this.showApprovalModal = true;
  }

  closeApprovalModal() {
    this.showApprovalModal = false;
    this.selectedLimit = null;
    this.approvalAmount = null;
  }

  confirmApproval() {
    if (!this.selectedLimit || this.approvalAmount === null) return;

    this.isApproving = true;

    this.userService
      .approvePendingLimitUpgrade(this.uuid, {
        limit_id: this.selectedLimit.id,
        amount: this.approvalAmount,
      })
      .subscribe({
        next: () => {
          alert('Limit upgrade approved successfully.');
          this.closeApprovalModal();
          this.loadLimits();
        },
        error: () => {
          alert('Failed to approve limit upgrade.');
          this.isApproving = false;
        },
        complete: () => {
          this.isApproving = false;
        },
      });
  }

  openCreateModal() {
    this.createLimitAmount = null;
    this.showCreateModal = true;
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.createLimitAmount = null;
  }

  confirmCreate() {
    if (this.createLimitAmount === null) return;

    this.isCreating = true;

    this.userService
      .createOutboundLimit(this.uuid, {
        limit_amount: this.createLimitAmount,
      })
      .subscribe({
        next: () => {
          alert('Outbound limit created successfully.');
          this.closeCreateModal();
          this.loadLimits();
        },
        error: () => {
          alert('Failed to create outbound limit.');
          this.isCreating = false;
        },
        complete: () => {
          this.isCreating = false;
        },
      });
  }

  openMonthlyLimitModal() {
    this.monthlyLimitAmount = null;
    this.showMonthlyLimitModal = true;
  }

  closeMonthlyLimitModal() {
    this.showMonthlyLimitModal = false;
    this.monthlyLimitAmount = null;
  }

  confirmCreateMonthlyLimit() {
    if (this.monthlyLimitAmount === null) return;

    this.isCreatingMonthlyLimit = true;

    this.userService
      .createMonthlyInboundLimit(this.uuid, {
        limit_amount: this.monthlyLimitAmount,
      })
      .subscribe({
        next: () => {
          alert('Monthly inbound limit created successfully.');
          this.closeMonthlyLimitModal();
          this.loadLimits();
        },
        error: () => {
          alert('Failed to create monthly inbound limit.');
          this.isCreatingMonthlyLimit = false;
        },
        complete: () => {
          this.isCreatingMonthlyLimit = false;
        },
      });
  }

  rejectLimit(limit: any) {
    if (!confirm('Are you sure you want to reject this limit upgrade?')) return;

    this.isRejecting = true;

    this.userService
      .rejectPendingLimitUpgrade(this.uuid, {
        limit_id: limit.id,
      })
      .subscribe({
        next: () => {
          alert('Limit upgrade rejected successfully.');
          this.loadLimits();
        },
        error: () => {
          alert('Failed to reject limit upgrade.');
          this.isRejecting = false;
        },
        complete: () => {
          this.isRejecting = false;
        },
      });
  }

  deleteLimit(limit: any) {
    if (!confirm('Are you sure you want to delete this transaction limit?'))
      return;

    this.isDeleting = true;

    this.userService.deleteTransactionLimit(limit.id).subscribe({
      next: () => {
        alert('Transaction limit deleted successfully.');
        this.loadLimits();
      },
      error: () => {
        alert('Failed to delete transaction limit.');
        this.isDeleting = false;
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }
}
